import { CommonModule } from '@angular/common';
import { Component, OnInit, ViewChild, inject, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import type { Contact } from '@app/models/account.model';
import type { ContactFormGroup } from '@app/models/form.models';
import { ContactService } from '@app/services/admin/contact.service';
import { ToastService } from '@app/services/toast.service';
import { ButtonComponent } from '@app/ui-components/button/button.component';
import { DetailsPageHeaderComponent } from '@app/ui-components/details-page-header/details-page-header.component';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { finalize, switchMap } from 'rxjs';
import { DetailsTabComponent } from './details-tab/details-tab.component';
import { ProjectsTabComponent } from './projects-tab/projects-tab.component';

type Tab = 'details' | 'projects';
interface TabDef {
  key: Tab;
  label: string;
}
@Component({
  selector: 'app-contact-details',
  standalone: true,
  imports: [
    CommonModule,
    DetailsPageHeaderComponent,
    ButtonComponent,
    TranslatePipe,
    DetailsTabComponent,
    ProjectsTabComponent,
  ],
  templateUrl: './contact-details.component.html',
  styleUrl: './contact-details.component.scss',
})
export class ContactDetailsComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly contacts = inject(ContactService);
  private readonly toast = inject(ToastService);
  private readonly translate = inject(TranslateService);
  @ViewChild(DetailsTabComponent) detailsTab?: DetailsTabComponent;
  contactId: string | null = null;
  contact = signal<Contact | null>(null);

  formGroup?: ContactFormGroup;

  isLoading = true;
  isToggling = false;
  isResetting = false;
  isDeleting = false;
  isSaving = false;
  isCancelling = false;

  activeTab: Tab = 'details';
  tabs: ReadonlyArray<TabDef> = [
    { key: 'details', label: 'admin.contacts.tabs.details' },
    { key: 'projects', label: 'admin.contacts.tabs.projects' },
  ];

  ngOnInit(): void {
    this.contactId = this.route.snapshot.paramMap.get('id');
    if (!this.contactId) {
      this.goBack();
      return;
    }
    this.load();
  }

  private load(): void {
    this.isLoading = true;
    this.contacts
      .getContactById(this.contactId!)
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe({
        next: (c) => this.contact.set(c),
        error: () => {
          this.toast.error(this.translate.instant('admin.toasts.loadFailed'));
          this.goBack();
        },
      });
  }

  personalData(): Contact | null {
    return this.contact();
  }

  get isDeactivated(): boolean {
    return !!(this.contact() as any)?.deactivated;
  }

  onResetPassword(): void {
    const email = this.personalData()?.email?.trim();
    if (!email || this.isResetting) return;

    this.isResetting = true;
    this.contacts
      .requestPasswordReset(email)
      .pipe(finalize(() => (this.isResetting = false)))
      .subscribe({
        next: () =>
          this.toast.success(
            this.translate.instant('admin.toasts.resetPasswordSent')
          ),
        error: () =>
          this.toast.error(
            this.translate.instant('admin.toasts.resetPasswordFailed')
          ),
      });
  }

  onToggleActivation(): void {
    if (!this.contactId || this.isToggling) return;

    this.isToggling = true;
    const action: 'reactivate' | 'deactivate' = this.isDeactivated
      ? 'reactivate'
      : 'deactivate';

    const req$ =
      action === 'reactivate'
        ? this.contacts.reactivateContact(this.contactId)
        : this.contacts.deactivateContact(this.contactId);

    req$
      .pipe(
        switchMap(() => this.contacts.getContactById(this.contactId!)),
        finalize(() => (this.isToggling = false))
      )
      .subscribe({
        next: (c) => {
          this.contact.set(c);
          const okKey =
            action === 'reactivate'
              ? 'admin.toasts.userReactivated'
              : 'admin.toasts.userSuspended';
          this.toast.success(this.translate.instant(okKey));
        },
        error: () => {
          const errKey =
            action === 'reactivate'
              ? 'admin.toasts.userReactivateFailed'
              : 'admin.toasts.userSuspendFailed';
          this.toast.error(this.translate.instant(errKey));
        },
      });
  }

  onDelete(): void {
    if (!this.contactId || this.isDeleting) return;
    this.isDeleting = true;
    this.contacts
      .deleteContact(this.contactId)
      .pipe(finalize(() => (this.isDeleting = false)))
      .subscribe({
        next: () => {
          this.toast.success(this.translate.instant('admin.toasts.deleted'));
          this.goBack();
        },
        error: () =>
          this.toast.error(this.translate.instant('admin.toasts.deleteFailed')),
      });
  }

  onSaveDetails(): void {
    if (!this.contactId) return;
    const save$ = this.detailsTab?.save(this.contactId);
    if (!save$) return;

    this.isSaving = true;
    save$
      .pipe(
        switchMap(() => this.contacts.getContactById(this.contactId!)),
        finalize(() => (this.isSaving = false))
      )
      .subscribe({
        next: (c) => {
          this.contact.set(c);
          this.toast.success(this.translate.instant('admin.toasts.saved'));
        },
        error: () =>
          this.toast.error(this.translate.instant('admin.toasts.saveFailed')),
      });
  }

  onCancelDetails(): void {
    if (!this.contactId) return;
    this.isCancelling = true;
    this.contacts
      .getContactById(this.contactId)
      .pipe(finalize(() => (this.isCancelling = false)))
      .subscribe({
        next: (c) => this.contact.set(c),
        error: () =>
          this.toast.error(this.translate.instant('admin.toasts.loadFailed')),
      });
  }

  goBack(): void {
    const target = history.state?.from || '/admin/contacts';
    this.router.navigate([target]);
  }
  setTab(tab: Tab): void {
    this.activeTab = tab;
  }
  cancel(): void {
    this.formGroup?.reset?.();
  }
}
